import { LeagueStatus, Tournament, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { EmptyState, TournamentCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

interface TournamentsTabProps {
  tournaments: Tournament[];
  leagueStatus: LeagueStatus;
  isOwner: boolean;
  onCreateTournament: (name: string, type: TournamentType) => void;
  onTournamentPress: (tournamentId: string) => void;
  creatingTournament: boolean;
  colors: any;
}

const TOURNAMENT_TYPES: { type: TournamentType; icon: string; labelKey: string }[] = [
  { type: 'single_elimination', icon: 'trophy', labelKey: 'tournaments.singleElimination' },
  { type: 'double_elimination', icon: 'tournament', labelKey: 'tournaments.doubleElimination' },
  { type: 'round_robin', icon: 'sync-circle', labelKey: 'tournaments.roundRobin' },
  { type: 'swiss', icon: 'grid', labelKey: 'tournaments.swiss' },
];

export const TournamentsTab = ({
  tournaments,
  leagueStatus,
  isOwner,
  onCreateTournament,
  onTournamentPress,
  creatingTournament,
  colors,
}: TournamentsTabProps) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [tournamentName, setTournamentName] = useState('');
  const [selectedType, setSelectedType] = useState<TournamentType>('single_elimination');

  const canCreate = isOwner && leagueStatus !== 'completed';

  const handleCreate = () => {
    if (tournamentName.trim()) {
      onCreateTournament(tournamentName.trim(), selectedType);
      setTournamentName('');
      setModalVisible(false);
    }
  };

  return (
    <View style={styles.container}>
      {canCreate && (
        <TouchableOpacity
          style={[styles.createButton, { backgroundColor: colors.primary }]}
          onPress={() => setModalVisible(true)}
        >
          <Ionicons name="add-circle-outline" size={20} color={colors.primaryText} />
          <Text style={[styles.createButtonText, { color: colors.primaryText }]}>
            {t('leagues.createTournament')}
          </Text>
        </TouchableOpacity>
      )}

      <FlatList
        data={tournaments}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <TournamentCard
            tournament={item}
            onPress={() => onTournamentPress(item.id)}
            colors={colors}
          />
        )}
        contentContainerStyle={tournaments.length === 0 ? { flex: 1 } : styles.listContent}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="trophy-outline" size={52} color={colors.textTertiary} />}
            title={t('leagues.noTournamentsInLeague')}
            subtitle={canCreate ? t('leagues.addTournamentToStart') : undefined}
            colors={colors}
          />
        }
      />

      <Modal visible={modalVisible} transparent animationType="slide" onRequestClose={() => setModalVisible(false)}>
        <View style={[styles.modalOverlay, { backgroundColor: colors.overlay }]}>
          <View style={[styles.modalContent, { backgroundColor: colors.surface }]}>
            <Text style={[styles.modalTitle, { color: colors.text }]}>{t('leagues.createTournament')}</Text>

            <TextInput
              style={[styles.modalInput, { backgroundColor: colors.card, color: colors.text, borderColor: colors.border }]}
              placeholder={t('tournaments.tournamentName')}
              placeholderTextColor={colors.textTertiary}
              value={tournamentName}
              onChangeText={setTournamentName}
              autoFocus
            />

            <Text style={[styles.typeLabel, { color: colors.text }]}>{t('tournaments.type')}</Text>
            <View style={styles.typeGrid}>
              {TOURNAMENT_TYPES.map((tt) => (
                <TouchableOpacity
                  key={tt.type}
                  style={[
                    styles.typeCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: selectedType === tt.type ? colors.primary : colors.border,
                      borderWidth: selectedType === tt.type ? 2 : 1,
                    },
                  ]}
                  onPress={() => setSelectedType(tt.type)}
                >
                  <Text style={[styles.typeText, { color: colors.text }]}>{t(tt.labelKey)}</Text>
                  {selectedType === tt.type && (
                    <Ionicons name="checkmark-circle" size={18} color={colors.primary} />
                  )}
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[styles.submitButton, { backgroundColor: colors.primary }, creatingTournament && styles.disabled]}
                onPress={handleCreate}
                disabled={creatingTournament}
              >
                {creatingTournament ? (
                  <ActivityIndicator color={colors.primaryText} />
                ) : (
                  <Text style={[styles.submitButtonText, { color: colors.primaryText }]}>{t('common.create')}</Text>
                )}
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.cancelButton, { borderColor: colors.border }]}
                onPress={() => setModalVisible(false)}
              >
                <Text style={[styles.cancelButtonText, { color: colors.text }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  createButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 15,
    padding: 12,
    borderRadius: 10,
    gap: 8,
  },
  createButtonText: { fontSize: 15, fontWeight: '600' },
  listContent: { paddingBottom: 20 },
  modalOverlay: { flex: 1, justifyContent: 'center', padding: 20 },
  modalContent: { borderRadius: 16, padding: 20, gap: 16 },
  modalTitle: { fontSize: 20, fontWeight: 'bold', textAlign: 'center' },
  modalInput: { padding: 14, borderRadius: 10, fontSize: 16, borderWidth: 1 },
  typeLabel: { fontSize: 16, fontWeight: '600' },
  typeGrid: { gap: 8 },
  typeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 10,
  },
  typeText: { fontSize: 15 },
  buttonRow: { flexDirection: 'row', gap: 12 },
  submitButton: { flex: 1, padding: 14, borderRadius: 10, alignItems: 'center' },
  submitButtonText: { fontSize: 16, fontWeight: '600' },
  cancelButton: { flex: 1, padding: 14, borderRadius: 10, alignItems: 'center', borderWidth: 1 },
  cancelButtonText: { fontSize: 16, fontWeight: '500' },
  disabled: { opacity: 0.6 },
});
